import { Injectable } from '@angular/core';
import { TravelInfoService } from '@secca/case/components/case-travel-info/service/travel-info.service';
import { CaseService } from '@secca/core/services/case.service';
import { IncidentService } from '@secca/core/services/incident.service';
import { InsuranceService } from '@secca/core/services/insurance.service';
import { PlannedTravelService } from '@secca/core/services/planned-travel.service';
import { CaseStateService } from '@secca/core/state-services/case-state.service';
import { AtlasCaseValidationDialogData } from '@secca/shared/components/atlas-chatbot/case-validation-dialog/atlas-case-validation-dialog.component';
import {
  compactAtlasCaseContextData,
  formatIncidentCauseLevel,
  formatIncidentLocation,
  formatWinterSportEnabled,
  getMainDiagnosisText,
  serializeCaseValidationLocation,
  serializePurposesOfTravelForAtlas
} from '@secca/shared/helpers/atlas/atlas-case-context-format.helper';
import { enforceContextFieldAvailability } from '@secca/shared/helpers/atlas/atlas-context-field-settings.helper';
import { formatDisplayDate, serializeDateForAtlas } from '@secca/shared/helpers/atlas/atlas-date-format-helper';
import { toNonEmptyString } from '@secca/shared/helpers/string-helper';
import { AtlasChatCaseContextData } from '@secca/shared/models/atlas/atlas-chat.model';
import {
  AtlasContextFieldSettings,
  AtlasContextFieldValues,
  AtlasContextInputsDialogData,
  EndUserPiiSuggestionContext
} from '@secca/shared/models/atlas/atlas-chatbot.model';
import { DocumentPIIEntityInput } from '@secca/shared/models/atlas/atlas-document-edit-and-read.model';
import { AtlasCaseData } from '@secca/shared/models/atlas/atlas-template.model';
import { Case } from '@secca/shared/models/case';
import { CustomerProfile } from '@secca/shared/models/customerProfile';
import { StakeholderTypeEnum } from '@secca/shared/models/enums';
import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AtlasCaseContextService {
  constructor(
    private caseService: CaseService,
    private caseStateService: CaseStateService,
    private incidentService: IncidentService,
    private insuranceService: InsuranceService,
    private plannedTravelService: PlannedTravelService,
    private travelInfoService: TravelInfoService
  ) {}

  async buildContextInputsDialogData(
    caseId: string | null,
    caseNumber: string,
    settings: AtlasContextFieldSettings
  ): Promise<AtlasContextInputsDialogData> {
    const plannedTravel = caseId ? await this.getCurrentPlannedTravel(caseId) : null;
    const plannedTravelSubPlans = caseId ? await this.getCurrentPlannedTravelSubPlans(caseId) : [];
    const incident = caseId ? await this.getCurrentIncident(caseId) : null;
    const plannedTravelDestinations = this.getPlannedTravelDestinations(plannedTravel, plannedTravelSubPlans);

    const values: AtlasContextFieldValues = {
      customerProfile: await this.getCurrentCustomerProfileName(caseId),
      endUserNationality: await this.getCurrentEndUserNationality(caseId),
      productName: await this.getCurrentCustomerProduct(caseId),
      insurancePeriodFrom: await this.getCurrentInsurancePeriodFrom(caseId),
      insurancePeriodTo: await this.getCurrentInsurancePeriodTo(caseId),
      winterSportEnabled: formatWinterSportEnabled(incident?.winterSport),
      incidentLocation: formatIncidentLocation(incident?.incidentLocation),
      incidentCauseLevel1: formatIncidentCauseLevel(incident, 1),
      incidentCauseLevel2: formatIncidentCauseLevel(incident, 2),
      incidentCauseLevel3: formatIncidentCauseLevel(incident, 3),
      incidentDescription: toNonEmptyString(incident?.description),
      mainDiagnosis: getMainDiagnosisText(incident),
      departureDate: formatDisplayDate(plannedTravel?.departureDate),
      arrivalDate: formatDisplayDate(plannedTravel?.arrivalDate),
      travelDestination: plannedTravelDestinations.length ? plannedTravelDestinations.join('; ') : null,
      coTravellers: toNonEmptyString(plannedTravel?.coTravellers),
      purposeOfTravel: plannedTravel?.purposesOfTravel?.length ? plannedTravel.purposesOfTravel.join(', ') : null,
      reporter: await this.getCurrentReporterName(caseId),
      incidentDate: formatDisplayDate(incident?.incidentDate)
    };

    const enforcedSettings = enforceContextFieldAvailability(settings, values);

    return {
      settings: { ...enforcedSettings },
      values,
      caseNumber
    };
  }

  async buildCaseValidationDialogData(
    caseId: string | null,
    caseNumber: string
  ): Promise<AtlasCaseValidationDialogData> {
    const plannedTravel = caseId ? await this.getCurrentPlannedTravel(caseId) : null;
    const incident = caseId ? await this.getCurrentIncident(caseId) : null;
    const personInsurance = caseId ? await this.getCurrentPersonInsurance(caseId) : null;
    const customerProfile = await this.getCurrentCustomerProfileName(caseId);
    const customerProduct = await this.getCurrentCustomerProduct(caseId);

    const insurancePeriodFrom = personInsurance?.insurancePeriodFrom || personInsurance?.insuranceLookupPolicy?.insurancePeriodStart;
    const insurancePeriodTo = personInsurance?.insurancePeriodTo || personInsurance?.insuranceLookupPolicy?.insurancePeriodEnd;
    const plannedDepartureDate = plannedTravel?.departureDate;
    const incidentDate = incident?.incidentDate;

    return {
      caseNumber,
      request: {
        insurance_period_from: serializeDateForAtlas(insurancePeriodFrom) || '',
        insurance_period_to: serializeDateForAtlas(insurancePeriodTo) || '',
        planned_departure_date: serializeDateForAtlas(plannedDepartureDate) || '',
        incident_date: serializeDateForAtlas(incidentDate) || '',
        customer_profile: customerProfile || '',
        customer_product: customerProduct || '',
        incident_location: serializeCaseValidationLocation(incident?.incidentLocation) || '',
        purposes_of_travel: serializePurposesOfTravelForAtlas(plannedTravel?.purposesOfTravel) || []
      },
      fields: {
        insurancePeriodFrom: formatDisplayDate(insurancePeriodFrom),
        insurancePeriodTo: formatDisplayDate(insurancePeriodTo),
        plannedDepartureDate: formatDisplayDate(plannedDepartureDate),
        incidentDate: formatDisplayDate(incidentDate)
      }
    };
  }

  async buildAtlasCaseContextData(
    caseId: string | null,
    settings: AtlasContextFieldSettings
  ): Promise<AtlasChatCaseContextData | null> {
    if (!caseId) {
      return null;
    }

    const needsIncident = settings.winterSportEnabled || settings.incidentLocation ||
      settings.incidentCauseLevel1 || settings.incidentCauseLevel2 || settings.incidentCauseLevel3 ||
      settings.incidentDescription || settings.mainDiagnosis || settings.incidentDate;
    const needsPlannedTravel = settings.departureDate || settings.arrivalDate ||
      settings.travelDestination || settings.coTravellers || settings.purposeOfTravel;

    const [personInsurance, incident, plannedTravel, plannedTravelSubPlans, reporter] = await Promise.all([
      this.getCurrentPersonInsurance(caseId),
      needsIncident ? this.getCurrentIncident(caseId) : Promise.resolve(null),
      needsPlannedTravel ? this.getCurrentPlannedTravel(caseId) : Promise.resolve(null),
      settings.travelDestination ? this.getCurrentPlannedTravelSubPlans(caseId) : Promise.resolve([]),
      settings.reporter ? this.getCurrentReporterName(caseId) : Promise.resolve(null)
    ]);

    const [customerProfileName, customerProduct] = await Promise.all([
      this.getCustomerProfileNameFromPersonInsurance(personInsurance),
      this.getCustomerProductFromPersonInsurance(personInsurance)
    ]);

    return compactAtlasCaseContextData({
      customer_profile: customerProfileName || '-',
      product_name: customerProduct || '-',
      insurance_period_from: settings.insurancePeriodFrom
        ? formatDisplayDate(personInsurance?.insurancePeriodFrom || personInsurance?.insuranceLookupPolicy?.insurancePeriodStart)
        : null,
      insurance_period_to: settings.insurancePeriodTo
        ? formatDisplayDate(personInsurance?.insurancePeriodTo || personInsurance?.insuranceLookupPolicy?.insurancePeriodEnd)
        : null,
      incident_location: settings.incidentLocation ? formatIncidentLocation(incident?.incidentLocation) : null,
      incident_date: settings.incidentDate ? formatDisplayDate(incident?.incidentDate) : null,
      incident_cause_level_1: settings.incidentCauseLevel1 ? formatIncidentCauseLevel(incident, 1) : null,
      incident_cause_level_2: settings.incidentCauseLevel2 ? formatIncidentCauseLevel(incident, 2) : null,
      incident_cause_level_3: settings.incidentCauseLevel3 ? formatIncidentCauseLevel(incident, 3) : null,
      winter_sport_enabled: settings.winterSportEnabled ? formatWinterSportEnabled(incident?.winterSport) : null,
      incident_description: settings.incidentDescription ? toNonEmptyString(incident?.description) : null,
      main_diagnosis: settings.mainDiagnosis ? getMainDiagnosisText(incident) : null,
      departure_date: settings.departureDate ? formatDisplayDate(plannedTravel?.departureDate) : null,
      arrival_date: settings.arrivalDate ? formatDisplayDate(plannedTravel?.arrivalDate) : null,
      planned_travel_destinations: settings.travelDestination
        ? this.getPlannedTravelDestinations(plannedTravel, plannedTravelSubPlans)
        : null,
      co_travellers: settings.coTravellers ? toNonEmptyString(plannedTravel?.coTravellers) : null,
      purpose_of_travel: settings.purposeOfTravel && plannedTravel?.purposesOfTravel?.length
        ? plannedTravel.purposesOfTravel.join(', ')
        : null,
      reporter
    });
  }

  async buildAtlasTemplateCaseData(
    caseId: string,
    settings: AtlasContextFieldSettings
  ): Promise<AtlasCaseData> {
    const seccaCase = await this.getCurrentCase(caseId);
    const incidentId = seccaCase?.incident?.id;

    const [incident, plannedTravel, plannedTravelSubPlans, personInsurance, otherInsurance, stakeholders] = await Promise.all([
      incidentId ? this.loadOrNull(() => firstValueFrom(this.incidentService.getCaseIncident(incidentId))) : Promise.resolve(null),
      this.loadOrNull(() => firstValueFrom(this.plannedTravelService.getPlannedTravel(caseId))),
      this.loadOrNull(() => firstValueFrom(this.plannedTravelService.getPlannedTravelSubPlans(caseId))),
      this.loadOrNull(() => firstValueFrom(this.insuranceService.getPersonInsurance(caseId))),
      this.loadOrNull(() => firstValueFrom(this.caseService.getOtherInsurance(caseId))),
      this.loadOrNull(() => firstValueFrom(this.caseService.getStakeholdersOnCase(caseId)))
    ]);
    const plannedTravelDestinations = this.getPlannedTravelDestinations(plannedTravel, plannedTravelSubPlans || []);

    const caseData: AtlasCaseData = {
      arrivalDate: settings.arrivalDate ? serializeDateForAtlas(plannedTravel?.arrivalDate) || undefined : undefined,
      currentLocation: formatIncidentLocation(incident?.currentLocation) || undefined,
      coTravellers: settings.coTravellers ? toNonEmptyString(plannedTravel?.coTravellers) : undefined,
      departureDate: settings.departureDate ? serializeDateForAtlas(plannedTravel?.departureDate) || undefined : undefined,
      planned_travel_destinations: settings.travelDestination
        ? plannedTravelDestinations.length ? plannedTravelDestinations : undefined
        : undefined,
      incidentLocation: settings.incidentLocation ? formatIncidentLocation(incident?.incidentLocation) || undefined : undefined,
      incidentDate: settings.incidentDate ? serializeDateForAtlas(incident?.incidentDate) || undefined : undefined,
      incidentDescription: settings.incidentDescription ? toNonEmptyString(incident?.description) : undefined,
      insuranceLevel: toNonEmptyString(personInsurance?.insuranceLookupPolicy?.insuranceLevel) ||
        toNonEmptyString(personInsurance?.insuranceLevelId),
      policyNumber: toNonEmptyString(personInsurance?.insuranceLookupPolicy?.policyNumber) ||
        toNonEmptyString(personInsurance?.policyReference),
      purposeOfTravel: settings.purposeOfTravel && plannedTravel?.purposesOfTravel?.length
        ? plannedTravel.purposesOfTravel
        : undefined,
      otherInsurance: this.formatOtherInsurance(otherInsurance),
      reporter: settings.reporter ? this.getReporterName(stakeholders || []) || undefined : undefined,
      travelAgency: toNonEmptyString(plannedTravel?.travelAgency),
      travelSeverityInformation: await this.getTravelSeverityInformation(incident, seccaCase?.countryCode) || undefined
    };

    return await this.applyAdditionalTemplateContextFields(caseData, personInsurance, incident, settings);
  }

  async getCurrentEndUserNationalId(caseId: string | null): Promise<string | null> {
    if (!caseId) {
      return null;
    }

    try {
      const stakeholders = await firstValueFrom(this.caseService.getStakeholdersOnCase(caseId));
      const endUser = stakeholders?.find(stakeholder => stakeholder?.stakeholderType === StakeholderTypeEnum.endUser);
      return toNonEmptyString(endUser?.person?.nationalId);
    } catch {
      return null;
    }
  }

  async getCurrentEndUserPiiSuggestions(caseId: string | null, caseNumber: string): Promise<DocumentPIIEntityInput[]> {
    const details = await this.getCurrentEndUserSuggestionContext(caseId, caseNumber);
    return [
      details.caseNumber ? { entity_type: 'CASE_NUMBER', value: details.caseNumber } : null,
      details.fullName ? { entity_type: 'FULL_NAME', value: details.fullName } : null,
      details.nationalId ? { entity_type: 'NATIONAL_ID', value: details.nationalId } : null,
      details.address ? { entity_type: 'ADDRESS', value: details.address } : null
    ].filter((entity): entity is DocumentPIIEntityInput => entity != null);
  }

  async getCurrentIncident(caseId: string): Promise<any | null> {
    try {
      const activeCase = await this.getCurrentCase(caseId);
      if (!activeCase?.incident?.id) {
        return null;
      }

      return await firstValueFrom(this.incidentService.getCaseIncident(activeCase.incident.id));
    } catch {
      return null;
    }
  }

  async getCurrentPersonInsurance(caseId: string): Promise<any | null> {
    try {
      return await firstValueFrom(this.insuranceService.getPersonInsurance(caseId));
    } catch {
      return null;
    }
  }

  private async getCurrentCustomerProfileName(caseId: string | null): Promise<string | null> {
    const personInsurance = caseId ? await this.getCurrentPersonInsurance(caseId) : null;
    return this.getCustomerProfileNameFromPersonInsurance(personInsurance);
  }

  private async getCustomerProfileNameFromPersonInsurance(personInsurance: any | null): Promise<string | null> {
    try {
      if (!personInsurance?.customerProfileId) {
        return null;
      }

      const customerProfile = await firstValueFrom(
        this.caseService.getCustomerProfile(String(personInsurance.customerProfileId))
      );
      return this.formatCustomerProfile(customerProfile);
    } catch {
      return null;
    }
  }

  private formatCustomerProfile(profile: CustomerProfile): string | null {
    const name = profile?.profileName?.trim();
    return name || null;
  }

  private async getCurrentCustomerProduct(caseId: string | null): Promise<string | null> {
    const personInsurance = caseId ? await this.getCurrentPersonInsurance(caseId) : null;
    return this.getCustomerProductFromPersonInsurance(personInsurance);
  }

  private async getCustomerProductFromPersonInsurance(personInsurance: any | null): Promise<string | null> {
    try {
      const productId = personInsurance?.customerProduct;
      if (!productId) {
        return null;
      }

      const product = await firstValueFrom(this.caseService.getCustomerProduct(productId));
      if (typeof product === 'string') {
        return product.trim() || String(productId);
      }

      const productName = product?.name || product?.productName || product?.displayName;
      return productName ? String(productName) : String(productId);
    } catch {
      return null;
    }
  }

  private async getCurrentEndUserNationality(caseId: string | null): Promise<string | null> {
    if (!caseId) {
      return null;
    }

    try {
      const personInsurance = await this.getCurrentPersonInsurance(caseId);
      const nationality = personInsurance?.insuranceLookupPolicy?.policyHolder?.citizenshipCountry;
      return toNonEmptyString(nationality);
    } catch {
      return null;
    }
  }

  private async getCurrentInsurancePeriodFrom(caseId: string | null): Promise<string | null> {
    const personInsurance = caseId ? await this.getCurrentPersonInsurance(caseId) : null;
    return formatDisplayDate(
      personInsurance?.insurancePeriodFrom || personInsurance?.insuranceLookupPolicy?.insurancePeriodStart
    );
  }

  private async getCurrentInsurancePeriodTo(caseId: string | null): Promise<string | null> {
    const personInsurance = caseId ? await this.getCurrentPersonInsurance(caseId) : null;
    return formatDisplayDate(
      personInsurance?.insurancePeriodTo || personInsurance?.insuranceLookupPolicy?.insurancePeriodEnd
    );
  }

  private async getCurrentPlannedTravel(caseId: string): Promise<any | null> {
    try {
      return await firstValueFrom(this.plannedTravelService.getPlannedTravel(caseId));
    } catch {
      return null;
    }
  }

  private async getCurrentPlannedTravelSubPlans(caseId: string): Promise<any[]> {
    try {
      return await firstValueFrom(this.plannedTravelService.getPlannedTravelSubPlans(caseId));
    } catch {
      return [];
    }
  }

  private getPlannedTravelDestinations(plannedTravel: any | null, plannedTravelSubPlans: any[] = []): string[] {
    const destinations = [plannedTravel, ...(plannedTravelSubPlans || [])]
      .map(travelPlan => formatIncidentLocation(travelPlan?.destinationLocation))
      .filter((value): value is string => !!value);

    return Array.from(new Set(destinations));
  }

  private async getCurrentReporterName(caseId: string | null): Promise<string | null> {
    if (!caseId) {
      return null;
    }

    try {
      const stakeholders = await firstValueFrom(this.caseService.getStakeholdersOnCase(caseId));
      const reporter = stakeholders?.find(stakeholder =>
        stakeholder?.stakeholderType === StakeholderTypeEnum.reporter || stakeholder?.reporter === true
      );
      if (!reporter) {
        return null;
      }

      const firstName = reporter?.person?.firstName || '';
      const surname = reporter?.person?.surname || '';
      const fullName = `${firstName} ${surname}`.trim();
      if (fullName) {
        return fullName;
      }

      return toNonEmptyString(reporter?.company?.name);
    } catch {
      return null;
    }
  }

  private async getCurrentEndUserSuggestionContext(
    caseId: string | null,
    caseNumber: string
  ): Promise<EndUserPiiSuggestionContext> {
    if (!caseId) {
      return {
        caseNumber: null,
        fullName: null,
        nationalId: null,
        address: null
      };
    }

    try {
      const stakeholders = await firstValueFrom(this.caseService.getStakeholdersOnCase(caseId));
      const endUser = stakeholders?.find(stakeholder => stakeholder?.stakeholderType === StakeholderTypeEnum.endUser);
      const firstName = endUser?.person?.firstName || '';
      const surname = endUser?.person?.surname || '';
      const fullName = toNonEmptyString(`${firstName} ${surname}`.trim());
      const nationalId = toNonEmptyString(endUser?.person?.nationalId);
      const postalCodeCity = [endUser?.address?.postalCode, endUser?.address?.city].filter(Boolean).join(' ');
      const address = toNonEmptyString(
        [endUser?.address?.street, postalCodeCity, endUser?.address?.country?.name || endUser?.address?.homeCountry]
          .filter(Boolean)
          .join(', ')
      );

      return {
        caseNumber: toNonEmptyString(caseNumber),
        fullName,
        nationalId,
        address
      };
    } catch {
      return {
        caseNumber: toNonEmptyString(caseNumber),
        fullName: null,
        nationalId: null,
        address: null
      };
    }
  }

  private async applyAdditionalTemplateContextFields(
    caseData: AtlasCaseData,
    personInsurance: any | null,
    incident: any | null,
    settings: AtlasContextFieldSettings
  ): Promise<AtlasCaseData> {
    const updatedCaseData: AtlasCaseData = { ...caseData };

    if (settings.customerProfile) {
      const customerProfile = await this.getCustomerProfileNameFromPersonInsurance(personInsurance);
      if (customerProfile) {
        updatedCaseData.additionalNotes = this.appendAdditionalNote(
          updatedCaseData.additionalNotes,
          `Customer profile: ${customerProfile}`
        );
      }
    }

    if (settings.productName) {
      const productName = await this.getCustomerProductFromPersonInsurance(personInsurance);
      if (productName) {
        updatedCaseData.additionalNotes = this.appendAdditionalNote(
          updatedCaseData.additionalNotes,
          `Product name: ${productName}`
        );
      }
    }

    if (settings.insurancePeriodFrom) {
      const insurancePeriodFrom = serializeDateForAtlas(
        personInsurance?.insurancePeriodFrom || personInsurance?.insuranceLookupPolicy?.insurancePeriodStart
      );
      if (insurancePeriodFrom) {
        updatedCaseData.additionalNotes = this.appendAdditionalNote(
          updatedCaseData.additionalNotes,
          `Insurance period from: ${insurancePeriodFrom}`
        );
      }
    }

    if (settings.insurancePeriodTo) {
      const insurancePeriodTo = serializeDateForAtlas(
        personInsurance?.insurancePeriodTo || personInsurance?.insuranceLookupPolicy?.insurancePeriodEnd
      );
      if (insurancePeriodTo) {
        updatedCaseData.additionalNotes = this.appendAdditionalNote(
          updatedCaseData.additionalNotes,
          `Insurance period to: ${insurancePeriodTo}`
        );
      }
    }

    if (settings.mainDiagnosis) {
      const mainDiagnosis = getMainDiagnosisText(incident);
      if (mainDiagnosis) {
        updatedCaseData.additionalNotes = this.appendAdditionalNote(
          updatedCaseData.additionalNotes,
          `Main diagnosis: ${mainDiagnosis}`
        );
      }
    }

    return updatedCaseData;
  }

  private appendAdditionalNote(existingNotes: string | undefined, note: string): string {
    const trimmedExisting = toNonEmptyString(existingNotes);
    if (trimmedExisting?.split('\n').map(value => value.trim()).includes(note.trim())) {
      return trimmedExisting;
    }

    return trimmedExisting ? `${trimmedExisting}\n${note}` : note;
  }

  private async getTravelSeverityInformation(incident: any, fallbackCountryCode?: string): Promise<Record<string, unknown> | null> {
    const currentLocationCountryCode = incident && incident.currentLocation
      ? incident.currentLocation.countryCodeAlpha2
      : null;
    const incidentLocationCountryCode = incident && incident.incidentLocation
      ? incident.incidentLocation.countryCodeAlpha2
      : null;
    const countryCode = currentLocationCountryCode || incidentLocationCountryCode || fallbackCountryCode;
    if (!countryCode) {
      return null;
    }

    const travelInfo = await this.loadOrNull(() => firstValueFrom(this.travelInfoService.fetchTravelInfo(countryCode)));
    if (!travelInfo) {
      return null;
    }

    return {
      country_code: travelInfo.alpha2 || countryCode,
      country_name: travelInfo.countryName || '',
      last_updated: travelInfo.lastUpdated || '',
      source_url: travelInfo.sourceUrl || '',
      advisories: (travelInfo.travelInfo || []).map(item => ({
        severity: item?.severity || '',
        headline: item?.headLine || '',
        summary: item?.summary || '',
        body_text: item?.bodyText || ''
      }))
    };
  }

  private getReporterName(stakeholders: any[]): string | null {
    const reporter = stakeholders.find(stakeholder =>
      stakeholder?.stakeholderType === StakeholderTypeEnum.reporter || stakeholder?.reporter === true
    );
    if (!reporter) {
      return null;
    }

    const firstName = reporter?.person?.firstName || '';
    const surname = reporter?.person?.surname || '';
    const fullName = `${firstName} ${surname}`.trim();
    if (fullName) {
      return fullName;
    }

    return toNonEmptyString(reporter?.company?.name);
  }

  private formatOtherInsurance(otherInsurance: any): string | undefined {
    if (!otherInsurance) {
      return undefined;
    }

    const parts = [
      otherInsurance.hasOtherInsurance ? `hasOtherInsurance=${otherInsurance.hasOtherInsurance}` : '',
      otherInsurance.creditCardBank ? `creditCardBank=${otherInsurance.creditCardBank}` : '',
      otherInsurance.creditCardType ? `creditCardType=${otherInsurance.creditCardType}` : '',
      otherInsurance.insuranceCompany ? `insuranceCompany=${otherInsurance.insuranceCompany}` : '',
      otherInsurance.insurancePolicyNo ? `insurancePolicyNo=${otherInsurance.insurancePolicyNo}` : '',
      otherInsurance.insuranceProduct ? `insuranceProduct=${otherInsurance.insuranceProduct}` : '',
      otherInsurance.insuranceRemarks ? `insuranceRemarks=${otherInsurance.insuranceRemarks}` : '',
      otherInsurance.creditCardRemarks ? `creditCardRemarks=${otherInsurance.creditCardRemarks}` : ''
    ].filter(Boolean);

    return parts.length > 0 ? parts.join('; ') : undefined;
  }

  private async loadOrNull<T>(loadOperation: () => Promise<T>): Promise<T | null> {
    try {
      return await loadOperation();
    } catch {
      return null;
    }
  }

  private async getCurrentCase(caseId: string): Promise<Case | null> {
    return this.loadOrNull(() =>
      firstValueFrom(
        this.caseStateService.getCase().pipe(
          filter((activeCase): activeCase is Case => !!activeCase && String(activeCase.id) === String(caseId)),
          take(1),
          timeout(1000)
        )
      )
    );
  }
}
